<script lang="ts">
	/**
	 * Site footer, following the live dutchpassion.co.th structure: link columns
	 * beside the logo, then a legal bar.
	 *
	 * The live site has a "Newsletter" column with a subscribe form. It is
	 * deliberately omitted: a form that silently does nothing is worse than no form
	 * at all. There is a Worker to receive it now (`/api/contact` in
	 * `src/worker.ts` is the pattern), so what it still needs is a mailing-list
	 * provider and a decision about consent wording.
	 *
	 * The live Thai footer leaves these columns in English; they are translated
	 * here.
	 *
	 * Every link is resolved through the category list or the page registry and
	 * dropped when it does not exist in this locale — a footer is where people go
	 * when the nav has failed them, so it is the worst place to hand out 404s.
	 */
	import { m } from '$lib/paraglide/messages';
	import { reloadIfWordPress } from '$lib/wordpress-links';
	import { page } from '$app/state';
	import { getLocale } from '$lib/paraglide/runtime';
	import { legalPath } from '$lib/services/legal';
	import { pagePath } from '$lib/pages';
	import { getCategoriesContext } from '$lib/context';
	import { isActivePath } from '$lib/helpers/nav';
	import { prefixPath, type Locale } from '$lib/i18n';
	import Logo from './Logo.svelte';
	import SocialLinks from './SocialLinks.svelte';

	const categoriesOf = getCategoriesContext();
	const categories = $derived(categoriesOf());
	const currentPath = $derived(page.url.pathname);

	/**
	 * A curated subset of categories, in a fixed order.
	 *
	 * Resolved through the category list rather than by building
	 * `/product-category/<english-slug>` paths: WPML translates those slugs, so
	 * the constructed Thai URLs would 404.
	 */
	const seedLinks = $derived(
		[
			{ key: 'feminised-seeds', label: m.feminized_seeds() },
			{ key: 'autoflower-seeds', label: m.autoflower_seeds() },
			{ key: 'high-thc-seeds', label: m.high_thc_seeds() },
			{ key: 'cannabis-seeds', label: m.view_all_seeds() }
		]
			.map((entry) => {
				const category = categories.find((c) => c.key === entry.key);
				return category ? { label: entry.label, href: category.fullSlug } : null;
			})
			.filter((link) => link !== null)
			// The comparison table is not a category, so it is appended rather than
			// resolved through the list above. It exists in every locale at the same
			// path — see the note in `routes/compare/+page.svelte`.
			.concat([{ label: m.compare_seeds(), href: prefixPath('/compare', getLocale() as Locale) }])
	);

	/** Resolved through the page registry — see UtilityBar for why. */
	const aboutLinks = $derived(
		[
			{ key: 'about', label: m.about_dutch_passion() },
			{ key: null, label: m.my_account(), href: '/my-account' }, // WordPress-served
			{ key: 'faq', label: m.faq() },
			{ key: 'contact', label: m.contact_us() }
		]
			.map((entry) => {
				if (!entry.key) return { label: entry.label, href: entry.href! };
				const path = pagePath(entry.key, getLocale());
				return path ? { label: entry.label, href: path } : null;
			})
			.filter((link) => link !== null)
	);

	/**
	 * News, stockists and the sitemap.
	 *
	 * These were missing entirely: the header offers all three, and the footer —
	 * the one place people look for a complete index of a site — listed none of
	 * them.
	 */
	const otherLinks = $derived(
		[
			{ label: m.news_and_blog(), href: (page.data.blogHref as string) ?? '/learn' },
			{ label: m.resellers(), href: pagePath('resellers', getLocale()) },
			// In all eight locales now. The drop-if-missing filter below still
			// applies: `pagePath` returns undefined for a page a locale lacks.
			{ label: m.legal_status_link(), href: pagePath('cannabis-law', getLocale()) },
			{ label: m.sitemap(), href: prefixPath('/sitemap', getLocale() as Locale) }
		]
			// Same drop-if-missing shape as the lists above, rather than a type
			// predicate — paraglide returns a branded `LocalizedString`, not `string`.
			.map((link) => (link.href ? { label: link.label, href: link.href } : null))
			.filter((link) => link !== null)
	);

	/**
	 * The legal bar. Thai has no translated privacy policy on the live site, so
	 * that link is English-only rather than pointing at a page that does not
	 * exist.
	 */
	const legalLinks = $derived(
		[
			{ key: 'terms', label: m.terms_and_conditions() },
			{ key: 'privacy', label: m.privacy_policy() },
			{ key: 'contact', label: m.contact_us() }
		]
			.map((entry) => {
				// Terms and privacy are markdown with their own registry, so
				// `pagePath` has never known them and both links were being dropped
				// here — the legal bar rendered as "Contact us" alone.
				const path =
					entry.key === 'contact'
						? pagePath(entry.key, getLocale())
						: legalPath(entry.key as 'terms' | 'privacy', getLocale());
				return path ? { label: entry.label, href: path } : null;
			})
			.filter((link) => link !== null)
	);

	const columns = $derived([
		{ heading: 'Dutch Passion Thailand', links: aboutLinks },
		{ heading: m.seed_type(), links: seedLinks },
		{ heading: m.other_links(), links: otherLinks }
	]);

	/**
	 * Baked at build time, because the whole site is prerendered — the footer will
	 * name the year of the last deploy rather than the current one until the site
	 * is rebuilt. Acceptable for a copyright line, and the alternative (reading the
	 * clock in the browser) trades a stale year for a hydration mismatch.
	 */
	const year = new Date().getFullYear();
</script>

<!--
	Ink footer, anchoring a page that is otherwise white throughout.

	No top margin: it would show the page colour as a stripe above the footer on
	every page. Separation comes from the colour change alone — except against the
	homepage, whose closing seed-types section is also `bg-ink-500`. `border-white/10`
	is the seam that stops those two merging into one ~1200px dark block.

	Text colours here are literal `gray-300`/`gray-400`, not `text-muted`. The
	semantic tokens are paired with light surfaces by design (see `app.css`), and
	`--color-muted` on ink is 2.37:1 — below AA. This matches how `PageHeader` and
	the About facts band already handle their ink blocks.
-->
<footer class="border-t border-white/10 bg-ink-500 text-gray-300">
	<div class="mx-auto max-w-7xl px-6 py-14">
		<div class="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
			<!-- Brand column: who this is and how to reach them, before the link lists. -->
			<div class="lg:pr-6">
				<Logo small inverse />
				<p class="mt-5 max-w-xs text-sm text-gray-400">{m.footer_tagline()}</p>

				<dl class="mt-5 grid gap-1 text-sm">
					<dt class="sr-only">{m.contact_us()}</dt>
					<dd>
						<a
							href="mailto:info@dutchpassion.co.th"
							class="text-brand-500 transition-colors hover:text-brand-400"
						>
							info@dutchpassion.co.th
						</a>
					</dd>
					<dd class="text-gray-400">{m.opening_hours()}</dd>
				</dl>

				<div class="mt-5">
					<SocialLinks tone="inverse" />
				</div>
			</div>

			<!--
				The column labels are `<p>`, not `<h2>`.

				They used to be headings, which put three of them into the document
				outline of all 64 pages — so every page's outline read as if "Seed
				type" and "Other links" were sections of its content, when they are
				furniture that happens to sit at the bottom. The label still has to be
				programmatically tied to its list for anyone navigating by landmark, so
				it keeps an `id` and the `<nav>` points at it: same association, no
				entry in the outline.
			-->
			{#each columns as column, i (column.heading)}
				<nav aria-labelledby={`footer-column-${i}`}>
					<p
						id={`footer-column-${i}`}
						class="mb-4 text-sm font-semibold tracking-wide text-white uppercase"
					>
						{column.heading}
					</p>
					<ul class="grid gap-2.5">
						{#each column.links as link (link.label)}
							{@const active = isActivePath(currentPath, link.href)}
							<li>
								<a
									href={link.href}
									data-sveltekit-reload={reloadIfWordPress(link.href)}
									aria-current={active ? 'page' : undefined}
									class="text-sm transition-colors hover:text-brand-400 {active
										? 'font-semibold text-brand-400'
										: 'text-gray-300'}"
								>
									{link.label}
								</a>
							</li>
						{/each}
					</ul>
				</nav>
			{/each}
		</div>
	</div>

	<div class="border-t border-white/10 bg-ink-600">
		<div
			class="mx-auto flex max-w-7xl flex-col-reverse items-center justify-between gap-3 px-6 py-5 text-sm sm:flex-row"
		>
			<p class="text-gray-400">{m.copyright({ year })}</p>
			<ul class="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
				{#each legalLinks as link (link.label)}
					<li>
						<a href={link.href} class="text-gray-400 transition-colors hover:text-brand-400">
							{link.label}
						</a>
					</li>
				{/each}
			</ul>
		</div>
	</div>
</footer>
